import { BookmarkBorderOutlined, Check, Close, FeedOutlined, SavingsOutlined, SentimentSatisfiedAltOutlined, TaskAltOutlined, VerifiedOutlined } from '@mui/icons-material';
import { Box, Grid, List, ListItem, Skeleton, Typography, useMediaQuery, useTheme } from "@mui/material";
import { skipToken } from "@reduxjs/toolkit/query";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAWSWebsocket } from '../../../Hooks/useAWSWebsocket';
import { useMobileView } from "../../../Hooks/useMobileView";
import { useVeriff } from '../../../Hooks/useVeriff';
import { useGetApplicationsByTalentQuery } from "../../../Redux/api/applicationApi";
import { useGetJobsByStatusQuery, useGetManyJobsQuery } from "../../../Redux/api/jobApi";
import { useGetTalentDashboardQuery, useGetTalentQuery } from "../../../Redux/api/talentApi";
import { useAppDispatch, useAppSelector } from "../../../Redux/store";
import { KaziCard } from "../../../Shared Components/Kazi/Cards/KaziCard";
import DashboardLayout from '../../../Shared Components/Kazi/Layouts/DashboardLayout';
import { KaziDemoModal } from "../../../Shared Components/KaziDemoModal";
import { VerificationStatus } from '../../../Types/General';
import { AppSettings } from '../../../Utils/Settings';
import { IsNullOrEmpty, toPascalCase } from '../../../Utils/String';
import { getPartOfDay } from "../../../Utils/Time";
import { TalentCreationModal } from "./ProfileCreation/Talent/TalentCreationModal";
import { useUser } from '../../../Hooks/useUser';

export const TalentDashboard = () => {
    /* ******************** Hooks ******************** */
    const { user } = useUser();
    const navigate = useNavigate();
    const { mountVeriff, getVerificationDetails } = useVeriff();
    const theme = useTheme();
    const [modalOpen, setModalOpen] = useState(false);
    const [showDemo, setShowDemo] = useState(false);
    const {
        data: talentDashboardStats, 
        isLoading: talentDashboardStatsLoading, 
        isFetching: talentDashboardStatsFetching 
    } = useGetTalentDashboardQuery();
    const { isMobileView } = useMobileView();
    const match = useMediaQuery(theme.breakpoints.down('lg'));
    const twoColumnMatch = useMediaQuery(theme.breakpoints.down('md'));
    const oneColumnMatch = useMediaQuery(theme.breakpoints.down('sm'));
    /* ******************** Variables ******************** */    
    const verification_status = useAppSelector( state => state.Websocket.verification_status);
    const statsLoading = useMemo(() =>{
        return talentDashboardStatsLoading || talentDashboardStatsFetching;
    }, [talentDashboardStatsLoading, talentDashboardStatsFetching])
    
    const partOfDay = getPartOfDay();

    const handleDemoClicked = () => {
        if (isMobileView) window.open(AppSettings.KaziDemoUrl);
        else setShowDemo(true);
    }
    const column1 = [
        {
            title: talentDashboardStats?.profileComplete ? "Profile complete" : "Complete your profile",
            progress:  talentDashboardStats?.profileCompleteness,
            subtitle: "A complete profile allows Clients to learn about your skills and easily contact you.",
            list: (talentDashboardStats?.profileCompleteness ?? 0)< 100 && (
            <List dense>
                <ListItem >{talentDashboardStats?.completedSections.summary ? <Check fontSize="small" sx={{marginRight: 1}}/>: <Close fontSize="small" color="error" sx={{marginRight: 1}}/>}About me</ListItem>
                <ListItem >{talentDashboardStats?.completedSections.languages ? <Check fontSize="small" sx={{marginRight: 1}}/>: <Close fontSize="small" color="error" sx={{marginRight: 1}}/>}Languages</ListItem>
                <ListItem >{talentDashboardStats?.completedSections.expertise ? <Check fontSize="small" sx={{marginRight: 1}}/>: <Close fontSize="small" color="error" sx={{marginRight: 1}}/>}Expertise</ListItem>
                <ListItem>{talentDashboardStats?.completedSections.education ? <Check fontSize="small" sx={{marginRight: 1}}/>: <Close fontSize="small" color="error" sx={{marginRight: 1}}/>}Education</ListItem>
                <ListItem>{talentDashboardStats?.completedSections.experience ? <Check fontSize="small" sx={{marginRight: 1}}/>: <Close fontSize="small" color="error" sx={{marginRight: 1}}/>}Experience</ListItem>
                <ListItem>{talentDashboardStats?.completedSections.workPreferences ? <Check fontSize="small" sx={{marginRight: 1}}/>: <Close fontSize="small" color="error" sx={{marginRight: 1}}/>}Work Preferences</ListItem>
                <ListItem>{talentDashboardStats?.completedSections.profilePicture ? <Check fontSize="small" sx={{marginRight: 1}}/>: <Close fontSize="small" color="error" sx={{marginRight: 1}}/>}Profile Picture</ListItem>
            </List>
            ),
            icon: <SentimentSatisfiedAltOutlined />,
            button: talentDashboardStats?.profileComplete ? {label: "View profile", action: () => navigate('/profile/talent'), disabled: false}: {label: "Continue", action: () => setModalOpen(true), disabled: false},
            gridRow: "1 / span 4",
            gridColumn: "1",
            marginTop: 0,
            secondaryButton: !(talentDashboardStats?.profileComplete || IsNullOrEmpty(AppSettings.KaziDemoUrl)) ? {label: "Watch demo", action: handleDemoClicked, disabled: false, variant: 'text'} : undefined
        }, 
    ]
    const column2 = [
        {
            title: `${talentDashboardStats?.numberOfApplications} job ${talentDashboardStats?.numberOfApplications !== 1 ? "applications": "application"}`,
            subtitle: talentDashboardStats?.lastApplication ? `Last sent to ${ talentDashboardStats.lastApplication.client} on ${moment(talentDashboardStats.lastApplication.applicationDate).format('MMM DD, YYYY')}`: '',
            icon: <FeedOutlined />,
            button: (talentDashboardStats?.numberOfApplications ?? 0) > 0 ? {label: "View", action: () => navigate('/applications'), disabled: false} : undefined,
            gridRow: "1",
            gridColumn: "2",
            marginTop: twoColumnMatch ? 3 : 0,
        }, 
        {
            title: `${talentDashboardStats?.numberOfBookmarkedJobs} bookmarked ${talentDashboardStats?.numberOfBookmarkedJobs !== 1?"jobs":"job"}`,
            subtitle: (talentDashboardStats?.numberOfBookmarkedJobs ?? 0) > 0 ?`Last bookmark was ${talentDashboardStats?.lastBookmarkedJob?.title} posted by ${talentDashboardStats?.lastBookmarkedJob?.client}`:"",
            icon: <BookmarkBorderOutlined />,
            button: (talentDashboardStats?.numberOfBookmarkedJobs ?? 0) > 0 ? {label: "View", action: () => navigate('/bookmarks'), disabled: false}: undefined,
            gridRow: (talentDashboardStats?.numberOfApplications ?? 0) > 0 || talentDashboardStatsLoading ?  (twoColumnMatch ? "3" : "2") : "1",
            gridColumn: "2",
            marginTop:(talentDashboardStats?.numberOfApplications ?? 0) > 0 || talentDashboardStatsLoading || twoColumnMatch ? 3 : 17
        }, 
        {
            title: `${talentDashboardStats?.numberOfActiveJobs} active ${talentDashboardStats?.numberOfActiveJobs !== 1?"jobs":"job"}`,
            subtitle: '',
            icon: <TaskAltOutlined />,
            button: (talentDashboardStats?.numberOfActiveJobs ?? 0) > 0 ? {label: "View", action: () => navigate('/active-jobs'), disabled: false}: undefined,
            gridRow: "3",
            gridColumn: twoColumnMatch ? "1" : "2",
            marginTop: 3,
        }, 
    ]
    const column3 = [
        {
            title: "$0 in wallet",
            subtitle: "0 transactions",
            icon: <SavingsOutlined />,
            button: false ? 
                    {label: "View", action: () => navigate('/wallet'), disabled: false} : 
                    {label: "Connect account", action: () => navigate('/wallet'), disabled: true},
            gridRow: twoColumnMatch ? "2" : "1",
            gridColumn: twoColumnMatch ? "2" : "3",
            marginTop: twoColumnMatch ? 3 : 0,
            action: () => {},
            disabled: true
        }, 
        {
            icon: <VerifiedOutlined />,
            gridRow: twoColumnMatch ? "4" : "1 / span 2",
            gridColumn: twoColumnMatch ? "1" : "3",
            marginTop: twoColumnMatch ? 2 : 26,
            ...getVerificationDetails()
        },
        // {
        //     title: "Validate your skills",
        //     subtitle: "Validating your skills shows clients you're proficient in your knowledge. Earn a badge for your profile when you complete validation.",
        //     icon: <VerifiedUserOutlined />,
        //     button: {label: "Validate", action: () => {}, disabled: true},
        //     gridRow: "2 / span 3",
        //     gridColumn: "1",
        //     marginTop: (talentData?.applications?.length ?? 0) > 0 ? 10 : 5,
        // }, 
    ]
    const gridLayout = useMemo(() => {
        if (oneColumnMatch) return `1fr`
        if (twoColumnMatch) return `repeat(2, 1fr)`
        if (match) return `minmax(290px, 368px)`
        return `repeat(auto-fit, minmax(290px, 368px))`
    }, [match, twoColumnMatch, oneColumnMatch])
    /* ******************** Functions ******************** */
    /* ******************** Effects ******************** */
    useEffect(() =>{
        const veriffElement = document.querySelector('#veriff-root');

        if (!statsLoading 
            && verification_status !== VerificationStatus.verified 
            && verification_status !== VerificationStatus.pending
            && veriffElement  ) mountVeriff();
    }, [statsLoading, verification_status])
    /* ******************** JSX ******************** */
    return (
        <>
            <Grid>
                {
                    statsLoading ? 
                    <Skeleton variant='rectangular'sx={{marginBottom: 2, width: "248px"}}/> :
                    <Typography variant="lora-headline-small">                         
                        Good {`${partOfDay?.label}, ${toPascalCase(user?.first_name ?? '')}`} <img src={partOfDay?.icon} height={'20px'}/>
                    </Typography>
                }
                <DashboardLayout>
                    <Box 
                        className='DashboardColumn'
                    >
                    {
                        column1.map((card, idx) =>(
                                <KaziCard 
                                    key={idx}
                                    title={card.title}
                                    progress={card?.progress}
                                    list={card?.list}
                                    subtitle={card.subtitle}
                                    icon={card?.icon}
                                    button={card?.button}
                                    //@ts-ignore, TODO: comeback and fix the type error when you have time
                                    secondaryButton={card?.secondaryButton}
                                    isLoading={statsLoading}
                                />
                        ))
                    }
                    </Box>
                    <Box 
                        className='DashboardColumn'
                    >
                    {
                        column2.map((card, idx) =>(
                                <KaziCard 
                                    key={idx}
                                    title={card.title}
                                    subtitle={card.subtitle}
                                    icon={card?.icon}
                                    button={card?.button}
                                    //@ts-ignore, TODO: comeback and fix the type error when you have time
                                    secondaryButton={card?.secondaryButton}
                                    isLoading={statsLoading}
                                />
                        ))
                    }
                    </Box>
                    <Box 
                        className='DashboardColumn'
                    >
                    {
                        column3.map((card, idx) =>(
                                <KaziCard 
                                    key={idx}
                                    title={card.title}
                                    subtitle={card.subtitle}
                                    icon={card?.icon}
                                    button={card?.button}
                                    //@ts-ignore
                                    veriff={card?.veriff}
                                    //@ts-ignore, TODO: comeback and fix the type error when you have time
                                    secondaryButton={card?.secondaryButton}
                                    isLoading={statsLoading}
                                />
                        ))
                    }
                    </Box>
                </DashboardLayout>
            </Grid>
            <TalentCreationModal open={modalOpen} setOpen={setModalOpen} hasResume={user?.resume != undefined} />
            <KaziDemoModal 
                open={showDemo}
                setOpen={setShowDemo}
            />
        </>
    )
}
